/**
 * A rate card's live settings (TMMPDM-0088, tmm-api 0153): the Controls tab, the shared
 * effective-dated settings component. TMMPDM-0089 (tmm-api 0154): the four affiliate maximums are live
 * card controls too, scheduled with the rest of the row. The Controls banner comes from the envelope's `reviewSuggested`
 * alone; the money field's currency is the envelope's `currency`, never the card read's.
 * TMMPDM-0091 (tmm-api 0156, MM-D-139): the Tax tab is gone; tax is pay-api's (Stripe Tax).
 */
import { useCallback, useState } from 'react';
import { Alert } from '@mui/material';
import { adminApi } from '@/api/tmm/admin';
import { EffectiveDatedSettings } from '@/components/settings/EffectiveDatedSettings';
import { useCopy } from '@/hooks/useCopy';
import { useCardControlFields } from './cardControlFields';

export function CardControlsTab({ cardId }: { cardId: number }) {
  const { t } = useCopy();
  const [meta, setMeta] = useState<{ currency: string; reviewSuggested: boolean } | null>(null);
  const load = useCallback(async () => {
    const envelope = await adminApi.getCardControls(cardId);
    setMeta({ currency: envelope.currency, reviewSuggested: envelope.reviewSuggested });
    return envelope;
  }, [cardId]);
  const fields = useCardControlFields();
  return (
    <EffectiveDatedSettings
      title={t('cardSettings.controls.title', 'Controls')}
      fields={fields}
      {...(meta ? { currency: meta.currency } : {})}
      load={load}
      schedule={(body) => adminApi.scheduleCardControls(cardId, body)}
      withdraw={(rowId, note) => adminApi.withdrawCardControls(cardId, rowId, note)}
      banner={
        meta?.reviewSuggested ? (
          <Alert severity="warning">
            {t(
              'cardSettings.controls.reviewSuggested',
              "This card isn't in USD and still has the starting maximum AI spend. Check it and schedule the right amount.",
            )}
          </Alert>
        ) : undefined
      }
    />
  );
}
